// components/common/drawer/BottomDrawer/BottomDrawer.tsx

"use client";

import React, { useRef, useEffect } from "react";
import "./BottomDrawer.css";

interface BottomDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxHeight?: string;
  showCloseButton?: boolean;

  /**
   * When true:
   * - overlay click won't close
   * - swipe won't close
   * - close button won't close
   * Useful when input is focused (keyboard open)
   */
  preventClose?: boolean;
}

/**
 * ✅ Stable Production Bottom Drawer
 * - swipe only from header
 * - closes only if scrolled to top
 * - safe for mobile keyboard + textarea
 */
const BottomDrawer: React.FC<BottomDrawerProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  maxHeight = "80vh",
  showCloseButton = true,
  preventClose = false,
}) => {
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);

  /* ---------------- Overlay click ---------------- */
  const handleOverlayClick = () => {
    if (preventClose) return;
    onClose();
  };

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  /* ---------------- Swipe logic (HEADER ONLY) ---------------- */
  const onTouchStart = (e: React.TouchEvent) => {
    const y = e.touches[0].clientY;
    touchStartY.current = y;
    touchEndY.current = y; // reset to prevent ghost swipe
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndY.current = e.touches[0].clientY;
  };

  const onTouchEnd = () => {
    if (preventClose) return;
    if (!drawerRef.current) return;

    const isAtTop = drawerRef.current.scrollTop <= 0;
    const swipeDistance = touchEndY.current - touchStartY.current;

    // Only close if:
    // - user is at top of scroll
    // - swipe is intentional (> 90px)
    if (isAtTop && swipeDistance > 90) {
      onClose();
    }
  };

  /* ---------------- Reset scroll on open ---------------- */
  useEffect(() => {
    if (drawerRef.current && open) {
      drawerRef.current.scrollTop = 0;
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="bottom-drawer-overlay" onClick={handleOverlayClick}>
      <div
        ref={drawerRef}
        className="bottom-drawer"
        style={{ maxHeight }}
        onClick={stopPropagation}
      >
        {(title || showCloseButton) && (
          <div
            className="bottom-drawer__header"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {title && (
              <span className="bottom-drawer__title">
                {title}
              </span>
            )}

            {showCloseButton && (
              <button
                className="bottom-drawer__close"
                onClick={() => {
                  if (preventClose) return;
                  onClose();
                }}
                aria-label="Close"
              >
                ×
              </button>
            )}
          </div>
        )}

        <div className="bottom-drawer__body">
          {children}
        </div>

        {footer && (
          <div className="bottom-drawer__footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default BottomDrawer;